import {FC} from 'react'
import {keyBy, compact, uniq} from 'lodash'
import {useFormContext} from 'react-hook-form'
import {
  Details,
  Row,
  Header,
  Body,
  BodyItems,
  BodyErrors,
  ComplementText,
} from 'components/SearchForm/AdditionalSearchField'
import {MultiTagFilteringFieldRow} from 'components/SearchForm/MultiTagFilteringFieldRow'
import {JobName} from 'jbc-front/components/Text/JobName'
import {DateRow} from 'components/SearchForm/DateRow'
import {GroupsSelectBox} from 'components/SearchForm/GroupsSelectBox'
import {OfficesSelectBox} from 'components/SearchForm/OfficesSelectBox'
import {SearchSelectBox} from 'components/SearchForm/SearchSelectBox'
import {SearchTextBox} from 'components/SearchForm/SearchTextBox'
import {DateField} from 'components/reactHookForm/DateField'
import {useQuery} from 'hooks/graphql/useQuery'
import {withNull, withNullOpt} from 'libs/options'
import {ADDITIONAL_SEARCH_QUERY, AdditionalSearchQuery, Group} from './query'
import {AddedFields} from './AddedFields'
import styles from './AdditionalSearchField.scss'
import type {Schema} from './schema'

const groupFullName = (group: Group, groups: Record<string, Group>): string => {
  if (group.parentGroupId) {
    const parent = groups[group.parentGroupId]
    if (parent) {
      return `${groupFullName(parent, groups)}->${group.name}`
    }
  }
  return group.name
}

const joinedStatusOptions = [
  {value: '', label: '-'},
  {value: 'true', label: '加入'},
  {value: 'false', label: '未加入'},
  {value: 'null', label: '未入力'},
]

interface AdditionalSearchFieldProps {
  showJoinedStatusField?: boolean
}

export const AdditionalSearchField: FC<AdditionalSearchFieldProps> = ({showJoinedStatusField = true}) => {
  const {
    register,
    formState: {errors},
  } = useFormContext<Schema>()
  const {data, loading} = useQuery<AdditionalSearchQuery>(ADDITIONAL_SEARCH_QUERY, {fetchPolicy: 'network-only'})
  if (loading || !data?.client) {
    return null
  }

  return (
    <Details>
      <Row>
        <Header>適用事業所</Header>
        <Body>
          <BodyItems>
            <OfficesSelectBox name="offices" />
          </BodyItems>
        </Body>
      </Row>
      <Row>
        <Header>グループ</Header>
        <Body>
          <BodyItems>
            <GroupsSelectBox
              name="groups"
              options={withNullOpt(
                data.client.groups.map((group) => ({
                  value: group.id,
                  label: groupFullName(group, keyBy(data.client.groups, 'id')),
                  parentValue: group.parentGroupId,
                }))
              )}
            />
          </BodyItems>
        </Body>
      </Row>
      <MultiTagFilteringFieldRow
        NameWrapper={JobName}
        header="役職"
        name="positions"
        list={withNull(data.client.employeeConnections.positions)}
      />
      <MultiTagFilteringFieldRow
        NameWrapper={JobName}
        header="雇用形態"
        name="employmentTypes"
        list={withNull(data.client.employeeConnections.employmentTypes)}
      />
      <MultiTagFilteringFieldRow
        NameWrapper={JobName}
        header="職種"
        name="occupations"
        list={withNull(data.client.employeeConnections.occupations)}
      />
      {showJoinedStatusField && (
        <Row>
          <Header>加入状況</Header>
          <Body>
            <BodyItems className={styles.joinedStatus}>
              <div className={styles.joinedStatusField}>
                <div className={styles.joinedStatusFieldLabel}>健康保険</div>
                <SearchSelectBox name="joinedHealthInsurance" options={joinedStatusOptions} width={110} />
              </div>
              <div className={styles.joinedStatusField}>
                <div className={styles.joinedStatusFieldLabel}>厚生年金保険</div>
                <SearchSelectBox name="joinedWelfarePensionInsurance" options={joinedStatusOptions} width={110} />
              </div>
              <div className={styles.joinedStatusField}>
                <div className={styles.joinedStatusFieldLabel}>雇用保険</div>
                <SearchSelectBox name="joinedEmploymentInsurance" options={joinedStatusOptions} width={110} />
              </div>
            </BodyItems>
          </Body>
        </Row>
      )}
      <Row>
        <Header>年齢</Header>
        <Body>
          <BodyItems>
            <SearchTextBox {...register('ageFrom')} style={{width: 40}} maxLength={3} isError={!!errors.ageFrom} />
            <ComplementText>歳以上</ComplementText>
            <SearchTextBox {...register('ageTo')} style={{width: 40}} maxLength={3} isError={!!errors.ageTo} />
            <ComplementText>歳以下</ComplementText>
            <div className={styles.dateField}>
              <DateField name="ageBaseDate" displayJpDate={false} small />
            </div>
            <ComplementText>時点</ComplementText>
          </BodyItems>
          <BodyErrors>
            {uniq(compact([errors.ageFrom?.message, errors.ageTo?.message, errors.ageBaseDate?.message])).map(
              (message) => (
                <p key={message}>{message}</p>
              )
            )}
          </BodyErrors>
        </Body>
      </Row>
      <DateRow header="入社日" startName="joinedAtStart" endName="joinedAtEnd" />
      <AddedFields />
    </Details>
  )
}
